/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';
import {
  Users, Search, Filter, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Shield, Mail, Lock, Unlock,
  RefreshCw, AlertTriangle, Eye, ToggleLeft, ToggleRight,
  ShieldCheck, Trash2
} from 'lucide-react';
import RestoreSuspendedModal from './RestoreSuspendedModal';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import './UserManagement.css';

const UserManagement = () => {
  // ── Auth context (for role-gated UI) ────────────────────────────────────────
  const { hasRole } = useAdminAuth();
  const canManageConnect = hasRole && hasRole('SUPER_ADMIN');

  // ── Core State ──────────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ── Pagination ──────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // ── Filters ─────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [verticalFilter, setVerticalFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  // ── Detail Modal ────────────────────────────────────────────────────────────
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // ── Restore Suspended Modal ─────────────────────────────────────────────────
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreUser, setRestoreUser] = useState(null);

  // ── Delete User State ───────────────────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [adminTotpCode, setAdminTotpCode] = useState('');

  // ── Connect Access State (per-user Connect gate + printed-QR handle) ────────
  const [connectHandleInput, setConnectHandleInput] = useState('');
  const [connectSaving, setConnectSaving] = useState(false);

  // ── Travel State (Phase 1 Travel domain enable/role/partnerId) ─────────────
  const [travelRoleInput, setTravelRoleInput] = useState('rep');
  const [travelPartnerIdInput, setTravelPartnerIdInput] = useState('');
  const [travelSaving, setTravelSaving] = useState(false);

  // ── Data Fetching ───────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page, limit,
        ...(search && { search }),
        ...(entityTypeFilter && { entityType: entityTypeFilter }),
        ...(verticalFilter && { vertical: verticalFilter }),
        ...(verifiedFilter && { isVerified: verifiedFilter }),
        ...(activeFilter && { isActive: activeFilter })
      });
      const response = await adminApi.request(`/admin/users?${params.toString()}`);
      if (response.success) {
        setUsers(response.data.users);
        setTotalPages(response.data.pagination.pages);
        setTotalUsers(response.data.pagination.total);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, entityTypeFilter, verticalFilter, verifiedFilter, activeFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminApi.request('/admin/users/stats');
      if (response.success) setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    const timer = setTimeout(() => setPage(1), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Sync Connect handle input with the currently selected user.
  useEffect(() => {
    setConnectHandleInput(selectedUser?.connectHandle || '');
  }, [selectedUser?._id, selectedUser?.connectHandle]);

  // Sync Travel role / partnerId inputs with the selected user.
  useEffect(() => {
    const t = selectedUser?.travel || null;
    setTravelRoleInput((t && t.role) || 'rep');
    setTravelPartnerIdInput((t && t.partnerId) || '');
  }, [selectedUser?._id, selectedUser?.travel]);

  // ── Action Handlers ─────────────────────────────────────────────────────────
  const handleToggleActive = async (userId) => {
    try {
      setActionLoading(true);
      const response = await adminApi.request(`/admin/users/${userId}/toggle-active`, { method: 'POST' });
      if (response.success) {
        setSuccess(response.message);
        fetchUsers();
        if (selectedUser?._id === userId) {
          setSelectedUser({ ...selectedUser, isActive: response.data.isActive });
        }
      }
    } catch (err) {
      setError('Failed to toggle user status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyEmail = async (userId) => {
    try {
      setActionLoading(true);
      const response = await adminApi.request(`/admin/users/${userId}/verify-email`, { method: 'POST' });
      if (response.success) {
        setSuccess(response.message);
        fetchUsers();
        if (selectedUser?._id === userId) {
          setSelectedUser({ ...selectedUser, isVerified: true });
        }
      }
    } catch (err) {
      setError('Failed to verify user email');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Send password reset email to this user?')) return;
    try {
      setActionLoading(true);
      const response = await adminApi.request(`/admin/users/${userId}/reset-password`, { method: 'POST' });
      if (response.success) setSuccess(response.message);
    } catch (err) {
      setError('Failed to send password reset email');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnlockAccount = async (userId) => {
    try {
      setActionLoading(true);
      const response = await adminApi.request(`/admin/users/${userId}/unlock`, { method: 'POST' });
      if (response.success) {
        setSuccess(response.message);
        fetchUsers();
        if (selectedUser?._id === userId) {
          setSelectedUser({ ...selectedUser, failedLoginAttempts: 0, lockedUntil: null });
        }
      }
    } catch (err) {
      setError('Failed to unlock account');
    } finally {
      setActionLoading(false);
    }
  };

  // Save Connect access flag + handle. Backend enforces the
  // users.connect.manage permission; a 403 here means the current admin
  // doesn't have it.
  const handleSaveConnect = async (userId, body) => {
    try {
      setConnectSaving(true);
      setError(null);
      const response = await adminApi.request(
        `/admin/users/${userId}/connect`,
        { method: 'PATCH', body: JSON.stringify(body) }
      );
      if (response.success) {
        setSuccess('Connect access updated');
        fetchUsers();
        if (selectedUser?._id === userId) {
          setSelectedUser({
            ...selectedUser,
            connectAccess: response.data.connectAccess,
            connectHandle: response.data.connectHandle,
            connectAccessSetBy: response.data.connectAccessSetBy,
            connectAccessSetAt: response.data.connectAccessSetAt
          });
        }
      } else {
        setError(response.error || 'Failed to update Connect access');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to update Connect access';
      setError(msg);
    } finally {
      setConnectSaving(false);
    }
  };

  // Save Travel enable + role + partnerId. SUPER_ADMIN + users.travel.manage.
  const handleSaveTravel = async (userId, body) => {
    try {
      setTravelSaving(true);
      setError(null);
      const response = await adminApi.request(
        `/admin/users/${userId}/travel`,
        { method: 'PATCH', body: JSON.stringify(body) }
      );
      if (response.success) {
        setSuccess('Travel settings updated');
        fetchUsers();
        if (selectedUser?._id === userId) {
          setSelectedUser({ ...selectedUser, travel: response.data.travel });
        }
      } else {
        setError(response.error || 'Failed to update Travel');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to update Travel';
      setError(msg);
    } finally {
      setTravelSaving(false);
    }
  };

  const handleDisable2FA = async (userId) => {
    if (!window.confirm('Disable 2FA for this user? They will need to set it up again.')) return;
    try {
      setActionLoading(true);
      const response = await adminApi.request(`/admin/users/${userId}/disable-2fa`, { method: 'POST' });
      if (response.success) {
        setSuccess(response.message);
        fetchUsers();
        if (selectedUser?._id === userId) {
          setSelectedUser({ ...selectedUser, twoFactorEnabled: false, twoFactorVerified: false });
        }
      }
    } catch (err) {
      setError('Failed to disable 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Delete Handler ──────────────────────────────────────────────────────────
  const openDeleteModal = (user) => {
    setDeleteTarget(user);
    setDeleteReason('');
    setDeleteConfirmText('');
    setAdminTotpCode('');
    setError(null);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setDeleteReason('');
    setDeleteConfirmText('');
    setAdminTotpCode('');
    setError(null);
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText !== 'DELETE USER') {
      setError('Please type exactly: DELETE USER');
      return;
    }
    if (!deleteReason || deleteReason.trim().length < 10) {
      setError('Reason is required (minimum 10 characters)');
      return;
    }
    try {
      setDeleteLoading(true);
      setError(null);
      const response = await adminApi.request(
        `/admin/users/${deleteTarget._id}`,
        {
          method: 'DELETE',
          body: JSON.stringify({
            reason: deleteReason.trim(),
            totpCode: adminTotpCode || undefined
          })
        }
      );
      if (response.success) {
        closeDeleteModal();
        setShowModal(false);
        setSelectedUser(null);
        setSuccess(`✅ User ${deleteTarget.email} permanently deleted. All data wiped.`);
        fetchUsers();
        fetchStats();
      } else {
        setError(response.error || response.message || 'Deletion failed — please try again');
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.error === '2FA_REQUIRED') {
        setError('Your admin account has 2FA enabled. Please enter your 2FA code above and try again.');
      } else if (errData?.error === '2FA_INVALID') {
        setError('Invalid 2FA code — please enter a fresh code from your authenticator app.');
      } else {
        setError('Deletion failed — ' + (errData?.message || err.message || 'please try again'));
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getEntityTypeBadge = (type) => {
    const types = {
      individual: { label: 'Individual', color: 'blue' },
      organization: { label: 'Organization', color: 'purple' },
      institute: { label: 'Institute', color: 'green' },
      university: { label: 'University', color: 'orange' }
    };
    const info = types[type] || types.individual;
    return <span className={`entity-badge ${info.color}`}>{info.label}</span>;
  };

  const getVerticalBadge = (vertical) => {
    const colors = {
      WMS: 'cyan', iSeries: 'blue', Civil: 'amber',
      Stitching: 'pink', ERPNext: 'yellow'
    };
    return <span className={`vertical-badge ${colors[vertical] || 'gray'}`}>{vertical}</span>;
  };

  const canConfirmDelete =
    deleteConfirmText === 'DELETE USER' &&
    deleteReason.trim().length >= 10 &&
    !deleteLoading;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <AdminLayout showBackButton backPath="/admin/dashboard">
      <div className="user-management">

        {/* Header */}
        <div className="um-header">
          <div className="um-header-left">
            <Users className="um-header-icon" />
            <div>
              <h1>User Management</h1>
              <p>View and manage user accounts</p>
            </div>
          </div>
          <button className="um-refresh-btn" onClick={() => { fetchUsers(); fetchStats(); }}>
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="um-message error">
            <AlertTriangle className="w-4 h-4" />
            {error}
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}
        {success && (
          <div className="um-message success">
            <CheckCircle className="w-4 h-4" /> {success}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="um-stats-grid">
            <div className="um-stat-card">
              <div className="um-stat-value">{stats.total}</div>
              <div className="um-stat-label">Total Users</div>
            </div>
            <div className="um-stat-card">
              <div className="um-stat-value">{stats.verified}</div>
              <div className="um-stat-label">Verified</div>
            </div>
            <div className="um-stat-card">
              <div className="um-stat-value">{stats.active}</div>
              <div className="um-stat-label">Active</div>
            </div>
            <div className="um-stat-card">
              <div className="um-stat-value">{stats.recentSignups}</div>
              <div className="um-stat-label">Last 7 Days</div>
            </div>
            <div className="um-stat-card">
              <div className="um-stat-value">{stats.twoFactorEnabled}</div>
              <div className="um-stat-label">2FA Enabled</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="um-filters">
          <div className="um-search">
            <Search className="um-search-icon" />
            <input
              type="text"
              placeholder="Search by email or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="um-filter-group">
            <Filter className="w-4 h-4" />
            <select value={entityTypeFilter} onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(1); }}>
              <option value="">All Entity Types</option>
              <option value="individual">Individual</option>
              <option value="organization">Organization</option>
              <option value="institute">Institute</option>
              <option value="university">University</option>
            </select>
            <select value={verticalFilter} onChange={(e) => { setVerticalFilter(e.target.value); setPage(1); }}>
              <option value="">All Verticals</option>
              <option value="WMS">WMS</option>
              <option value="iSeries">iSeries</option>
              <option value="Civil">Civil</option>
              <option value="Stitching">Stitching</option>
              <option value="ERPNext">ERPNext</option>
              <option value="SkillCode">SkillCode</option>
            </select>
            <select value={verifiedFilter} onChange={(e) => { setVerifiedFilter(e.target.value); setPage(1); }}>
              <option value="">All Verification</option>
              <option value="true">Verified</option>
              <option value="false">Not Verified</option>
            </select>
            {/* ── Status filter — includes Deleted ── */}
            <select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="um-table-container">
          {loading ? (
            <div className="um-loading">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="um-empty">No users found matching your criteria</div>
          ) : (
            <table className="um-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Entity Type</th>
                  <th>Vertical</th>
                  <th>Status</th>
                  <th>2FA</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user._id} style={user.isDeleted === true ? {
                    background: '#FEF2F2', color: '#991B1B', opacity: 0.85
                  } : {}}>
                    <td className="um-user-cell">
                      <div className="um-user-info">
                        <div className="um-user-avatar" style={user.isDeleted === true ? {
                          background: '#FECACA', color: '#991B1B'
                        } : {}}>
                          {user.fullName?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <div className="um-user-name">{user.fullName}</div>
                          <div className="um-user-email">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{getEntityTypeBadge(user.entityType)}</td>
                    <td>{getVerticalBadge(user.vertical)}</td>
                    <td>
                      <div className="um-status-badges">
                        {user.isVerified ? (
                          <span className="status-badge verified"><CheckCircle className="w-3 h-3" /> Verified</span>
                        ) : (
                          <span className="status-badge unverified"><XCircle className="w-3 h-3" /> Unverified</span>
                        )}
                        {/* ── isDeleted check ── */}
                        {user.isDeleted === true ? (
                          <span className="status-badge" style={{
                            background: '#7F1D1D', color: 'white', fontSize: '11px',
                            padding: '2px 8px', borderRadius: '9999px'
                          }}>🗑️ Deleted</span>
                        ) : user.isSuspended || user.isBanned ? (
                          <span className="status-badge suspended"><Shield className="w-3 h-3" /> Suspended</span>
                        ) : !user.isActive ? (
                          <span className="status-badge inactive">Inactive</span>
                        ) : (
                          <span className="status-badge active-badge">Active</span>
                        )}
                        {user.lockedUntil && new Date(user.lockedUntil) > new Date() && (
                          <span className="status-badge locked"><Lock className="w-3 h-3" /> Locked</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {user.twoFactorEnabled && user.twoFactorVerified ? (
                        <span className="status-badge twofa-enabled"><Shield className="w-3 h-3" /> Enabled</span>
                      ) : (
                        <span className="status-badge twofa-disabled">Off</span>
                      )}
                    </td>
                    <td className="um-date">{formatDate(user.createdAt)}</td>
                    <td>
                      <div className="um-actions">
                        <button
                          className="um-action-btn view"
                          title="View Details"
                          onClick={() => { setSelectedUser(user); setShowModal(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {/* Hide toggle/delete for deleted users */}
                        {user.isDeleted !== true && (
                          <>
                            <button
                              className={`um-action-btn ${user.isActive ? 'deactivate' : 'activate'}`}
                              title={user.isActive ? 'Deactivate' : 'Activate'}
                              onClick={() => handleToggleActive(user._id)}
                              disabled={actionLoading}
                            >
                              {user.isActive ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                            </button>
                            <button
                              className="um-action-btn"
                              title="Delete User Permanently"
                              onClick={() => openDeleteModal(user)}
                              disabled={actionLoading}
                              style={{ color: '#DC2626' }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="um-pagination">
            <span className="um-pagination-info">
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalUsers)} of {totalUsers} users
            </span>
            <div className="um-pagination-controls">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="um-page-number">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ── User Detail Modal ─────────────────────────────────────────────── */}
        {showModal && selectedUser && (
          <div className="um-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="um-modal" onClick={e => e.stopPropagation()}>
              {/* Modal header — red for deleted users */}
              <div className="um-modal-header" style={selectedUser.isDeleted === true ? {
                background: '#FEF2F2', borderBottom: '1px solid #FECACA'
              } : {}}>
                <h2 style={selectedUser.isDeleted === true ? { color: '#991B1B' } : {}}>
                  {selectedUser.isDeleted === true ? '🗑️ Deleted User' : 'User Details'}
                </h2>
                <button className="um-modal-close" onClick={() => setShowModal(false)}>&times;</button>
              </div>
              <div className="um-modal-body">

                {/* Basic Information */}
                <div className="um-detail-section">
                  <h3>Basic Information</h3>
                  <div className="um-detail-grid">
                    <div className="um-detail-item">
                      <label>Full Name</label>
                      <span>{selectedUser.fullName}</span>
                    </div>
                    <div className="um-detail-item">
                      <label>Email</label>
                      <span>{selectedUser.email}</span>
                    </div>
                    <div className="um-detail-item">
                      <label>Entity Type</label>
                      {getEntityTypeBadge(selectedUser.entityType)}
                    </div>
                    <div className="um-detail-item">
                      <label>Vertical</label>
                      {getVerticalBadge(selectedUser.vertical)}
                    </div>
                    <div className="um-detail-item">
                      <label>User Type</label>
                      <span className="capitalize">{selectedUser.userType}</span>
                    </div>
                    <div className="um-detail-item">
                      <label>Created</label>
                      <span>{formatDate(selectedUser.createdAt)}</span>
                    </div>
                    {/* Show deletedAt if deleted */}
                    {selectedUser.isDeleted === true && selectedUser.deletedAt && (
                      <div className="um-detail-item">
                        <label>Deleted At</label>
                        <span style={{ color: '#DC2626', fontWeight: '600' }}>
                          {formatDate(selectedUser.deletedAt)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Account Status */}
                <div className="um-detail-section">
                  <h3>Account Status</h3>
                  <div className="um-detail-grid">
                    <div className="um-detail-item">
                      <label>Email Verified</label>
                      <span>{selectedUser.isVerified ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="um-detail-item">
                      <label>Active</label>
                      <span style={selectedUser.isDeleted === true ? {
                        color: '#DC2626', fontWeight: '600'
                      } : {}}>
                        {selectedUser.isDeleted === true
                          ? '🗑️ Deleted'
                          : selectedUser.isSuspended
                            ? 'Suspended'
                            : selectedUser.isActive ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="um-detail-item">
                      <label>2FA</label>
                      <span>{selectedUser.twoFactorEnabled && selectedUser.twoFactorVerified ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <div className="um-detail-item">
                      <label>Failed Login Attempts</label>
                      <span>{selectedUser.failedLoginAttempts || 0}</span>
                    </div>
                    {selectedUser.lockedUntil && (
                      <div className="um-detail-item">
                        <label>Locked Until</label>
                        <span>{formatDate(selectedUser.lockedUntil)}</span>
                      </div>
                    )}
                    {selectedUser.lastLoginAt && (
                      <div className="um-detail-item">
                        <label>Last Login</label>
                        <span>{formatDate(selectedUser.lastLoginAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* SkillDelivery Connect access — SUPER_ADMIN only.
                    Backend also enforces users.connect.manage; this hide is UI-only. */}
                {canManageConnect && (
                <div className="um-detail-section">
                  <h3>SkillDelivery Connect access</h3>
                  <div style={{
                    background: '#F9FAFB', border: '1px solid #E5E7EB',
                    borderRadius: '8px', padding: '16px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 10px', borderRadius: '999px',
                        fontSize: '11px', fontWeight: 600,
                        background: selectedUser.connectAccess ? '#D1FAE5' : '#F3F4F6',
                        color: selectedUser.connectAccess ? '#065F46' : '#6B7280'
                      }}>
                        {selectedUser.connectAccess ? 'ENABLED' : 'DISABLED'}
                      </span>
                      {selectedUser.connectAccessSetAt && (
                        <span style={{ fontSize: '11px', color: '#6B7280' }}>
                          Last changed {formatDate(selectedUser.connectAccessSetAt)}
                        </span>
                      )}
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '4px' }}>
                        Handle (printed-QR link: connect.skilldelivery.co.in/q/&lt;handle&gt;)
                      </label>
                      <input
                        type="text"
                        value={connectHandleInput}
                        onChange={(e) => setConnectHandleInput(e.target.value)}
                        placeholder="e.g. lalit"
                        style={{
                          width: '100%', padding: '6px 10px', border: '1px solid #D1D5DB',
                          borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace'
                        }}
                        disabled={connectSaving}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        className="um-action-btn-lg"
                        onClick={() => handleSaveConnect(selectedUser._id, {
                          handle: connectHandleInput.trim() || null
                        })}
                        disabled={connectSaving || (connectHandleInput.trim() || '') === (selectedUser.connectHandle || '')}
                      >
                        Save handle
                      </button>
                      {!selectedUser.connectAccess ? (
                        <button
                          className="um-action-btn-lg activate"
                          onClick={() => handleSaveConnect(selectedUser._id, {
                            enabled: true,
                            handle: connectHandleInput.trim() || undefined
                          })}
                          disabled={connectSaving}
                        >
                          Enable Connect access
                        </button>
                      ) : (
                        <button
                          className="um-action-btn-lg deactivate"
                          onClick={() => handleSaveConnect(selectedUser._id, { enabled: false })}
                          disabled={connectSaving}
                        >
                          Disable Connect access
                        </button>
                      )}
                    </div>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '10px', marginBottom: 0 }}>
                      Requires the <code>users.connect.manage</code> permission
                      (SUPER_ADMIN only).
                    </p>
                  </div>
                </div>
                )}

                {/* Travel Domain (Phase 1) */}
                {selectedUser.isDeleted !== true && (
                <div className="um-detail-section">
                  <h3>Travel Domain</h3>
                  <div style={{ padding: '12px', border: '1px solid #E5E7EB', borderRadius: '8px', background: '#FAFAFA' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>Status</span>
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                        background: selectedUser.travel?.enabled ? '#D1FAE5' : '#F3F4F6',
                        color: selectedUser.travel?.enabled ? '#065F46' : '#6B7280'
                      }}>
                        {selectedUser.travel?.enabled ? `ENABLED (${selectedUser.travel.role || '?'})` : 'DISABLED'}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '4px' }}>
                          Role
                        </label>
                        <select
                          value={travelRoleInput}
                          onChange={(e) => setTravelRoleInput(e.target.value)}
                          disabled={travelSaving}
                          style={{
                            width: '100%', padding: '6px 10px', border: '1px solid #D1D5DB',
                            borderRadius: '6px', fontSize: '13px', background: 'white'
                          }}
                        >
                          <option value="rep">rep</option>
                          <option value="partner">partner</option>
                          <option value="learner">learner</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#6B7280', display: 'block', marginBottom: '4px' }}>
                          Partner ID (optional)
                        </label>
                        <input
                          type="text"
                          value={travelPartnerIdInput}
                          onChange={(e) => setTravelPartnerIdInput(e.target.value)}
                          placeholder="e.g. PL123ABC"
                          disabled={travelSaving}
                          style={{
                            width: '100%', padding: '6px 10px', border: '1px solid #D1D5DB',
                            borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace'
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {!selectedUser.travel?.enabled ? (
                        <button
                          className="um-action-btn-lg activate"
                          onClick={() => handleSaveTravel(selectedUser._id, {
                            enabled: true,
                            role: travelRoleInput,
                            partnerId: travelPartnerIdInput.trim() || null
                          })}
                          disabled={travelSaving}
                        >
                          Enable Travel
                        </button>
                      ) : (
                        <>
                          <button
                            className="um-action-btn-lg"
                            onClick={() => handleSaveTravel(selectedUser._id, {
                              enabled: true,
                              role: travelRoleInput,
                              partnerId: travelPartnerIdInput.trim() || null
                            })}
                            disabled={travelSaving}
                          >
                            Update role/partnerId
                          </button>
                          <button
                            className="um-action-btn-lg deactivate"
                            onClick={() => handleSaveTravel(selectedUser._id, { enabled: false })}
                            disabled={travelSaving}
                          >
                            Disable Travel
                          </button>
                        </>
                      )}
                    </div>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '10px', marginBottom: 0 }}>
                      Requires the <code>users.travel.manage</code> permission
                      (SUPER_ADMIN only). Legacy ShubbhYatra reps are already
                      treated as Travel-enabled by the server; this toggle
                      makes the field authoritative on disk and surfaces role
                      selection + a partner link.
                    </p>
                  </div>
                </div>
                )}

                {/* Admin Actions */}
                <div className="um-detail-section">
                  <h3>Admin Actions</h3>
                  {selectedUser.isDeleted === true ? (
                    <div style={{
                      background: '#FEF2F2', border: '1px solid #FECACA',
                      borderRadius: '8px', padding: '20px', textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>🗑️</div>
                      <p style={{ color: '#991B1B', fontSize: '13px', fontWeight: '600', margin: '0 0 4px' }}>
                        Account Permanently Deleted
                      </p>
                      <p style={{ color: '#B91C1C', fontSize: '12px', margin: 0 }}>
                        All user data has been wiped. No further actions are available.
                      </p>
                    </div>
                  ) : (
                    <div className="um-action-buttons">
                      {!selectedUser.isVerified && (
                        <button className="um-action-btn-lg verify"
                          onClick={() => handleVerifyEmail(selectedUser._id)}
                          disabled={actionLoading}>
                          <Mail className="w-4 h-4" /> Verify Email
                        </button>
                      )}
                      <button
                        className={`um-action-btn-lg ${selectedUser.isActive ? 'deactivate' : 'activate'}`}
                        onClick={() => handleToggleActive(selectedUser._id)}
                        disabled={actionLoading}>
                        {selectedUser.isActive ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                        {selectedUser.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="um-action-btn-lg reset"
                        onClick={() => handleResetPassword(selectedUser._id)}
                        disabled={actionLoading}>
                        <Lock className="w-4 h-4" /> Send Password Reset
                      </button>
                      {(selectedUser.failedLoginAttempts >= 5 || selectedUser.lockedUntil) && (
                        <button className="um-action-btn-lg unlock"
                          onClick={() => handleUnlockAccount(selectedUser._id)}
                          disabled={actionLoading}>
                          <Unlock className="w-4 h-4" /> Unlock Account
                        </button>
                      )}
                      {selectedUser.twoFactorEnabled && (
                        <button className="um-action-btn-lg disable-2fa"
                          onClick={() => handleDisable2FA(selectedUser._id)}
                          disabled={actionLoading}>
                          <Shield className="w-4 h-4" /> Disable 2FA
                        </button>
                      )}
                      {(selectedUser.isSuspended || selectedUser.isBanned || !selectedUser.isActive) && (
                        <button
                          className="um-action-btn-lg activate"
                          onClick={() => {
                            const userToRestore = { ...selectedUser };
                            setShowModal(false);
                            setSelectedUser(null);
                            setTimeout(() => {
                              setRestoreUser(userToRestore);
                              setShowRestoreModal(true);
                            }, 100);
                          }}
                          disabled={actionLoading}
                          style={{ background: '#ecfeff', color: '#0891b2', border: '1px solid #a5f3fc' }}>
                          <ShieldCheck className="w-4 h-4" /> Restore Suspended
                        </button>
                      )}
                      <div style={{
                        borderTop: '1.5px solid #FEE2E2', marginTop: '16px',
                        paddingTop: '16px', width: '100%'
                      }}>
                        <p style={{
                          fontSize: '11px', color: '#9CA3AF', marginBottom: '10px',
                          textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600'
                        }}>
                          ⚠️ Danger Zone
                        </p>
                        <button
                          className="um-action-btn-lg"
                          onClick={() => {
                            setShowModal(false);
                            setTimeout(() => openDeleteModal(selectedUser), 100);
                          }}
                          disabled={actionLoading}
                          style={{
                            background: '#FEF2F2', color: '#DC2626',
                            border: '1.5px solid #FECACA', width: '100%'
                          }}>
                          <Trash2 className="w-4 h-4" />
                          Delete User Permanently
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Restore Suspended Modal ───────────────────────────────────────── */}
        {showRestoreModal && restoreUser && (
          <RestoreSuspendedModal
            user={restoreUser}
            onClose={() => { setShowRestoreModal(false); setRestoreUser(null); }}
            onSuccess={() => {
              setShowRestoreModal(false);
              setRestoreUser(null);
              setSuccess('Account restored successfully');
              fetchUsers();
              fetchStats();
            }}
          />
        )}

        {/* ── Delete User Confirmation Modal ────────────────────────────────── */}
        {showDeleteModal && deleteTarget && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '20px'
          }}>
            <div style={{
              background: 'white', borderRadius: '12px', padding: '32px',
              maxWidth: '520px', width: '100%',
              boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
              maxHeight: '90vh', overflowY: 'auto'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>🗑️</div>
                <h2 style={{ color: '#DC2626', fontSize: '22px', margin: '0 0 6px' }}>
                  Delete User Permanently
                </h2>
                <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>
                  <strong>{deleteTarget.fullName}</strong> · {deleteTarget.email}
                </p>
                <p style={{ color: '#6B7280', fontSize: '12px', margin: '4px 0 0' }}>
                  {deleteTarget.entityType} · {deleteTarget.vertical}
                </p>
              </div>

              <div style={{
                background: '#FEF2F2', border: '1px solid #FECACA',
                borderRadius: '8px', padding: '14px 16px', marginBottom: '12px'
              }}>
                <p style={{
                  color: '#7F1D1D', fontSize: '12px', fontWeight: '700',
                  marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'
                }}>🗑️ Permanently deleted:</p>
                <p style={{ color: '#B91C1C', fontSize: '12px', lineHeight: '1.7', margin: 0 }}>
                  Profile · Goals · Roadmap · Workspace · Documents ·
                  xConnect connections · Community messages ·
                  Knowledge Base entries · LinkedIn network data ·
                  Match history · All platform activity
                </p>
              </div>

              <div style={{
                background: '#F0FDF4', border: '1px solid #BBF7D0',
                borderRadius: '8px', padding: '14px 16px', marginBottom: '20px'
              }}>
                <p style={{
                  color: '#14532D', fontSize: '12px', fontWeight: '700',
                  marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'
                }}>✅ Retained (anonymised):</p>
                <p style={{ color: '#166534', fontSize: '12px', lineHeight: '1.7', margin: 0 }}>
                  User record (isDeleted: true, PII removed) ·
                  Email + Phone (secured, internal only) ·
                  SkillBot conversations (anonymised, for AI evaluation) ·
                  Audit trail of deletion
                </p>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{
                  fontSize: '13px', fontWeight: '600', color: '#374151',
                  display: 'block', marginBottom: '6px'
                }}>
                  Reason for deletion <span style={{ color: '#DC2626' }}>*</span>
                  <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }}>
                    (min 10 chars — saved to audit log)
                  </span>
                </label>
                <textarea
                  value={deleteReason}
                  onChange={e => setDeleteReason(e.target.value)}
                  placeholder="e.g. User requested deletion via support email..."
                  rows={2}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: `1px solid ${deleteReason.trim().length >= 10 ? '#10B981' : '#E5E7EB'}`,
                    borderRadius: '6px', fontSize: '13px',
                    resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit'
                  }}
                />
                <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '4px 0 0' }}>
                  {deleteReason.trim().length}/10 minimum characters
                </p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  fontSize: '13px', fontWeight: '600', color: '#374151',
                  display: 'block', marginBottom: '6px'
                }}>
                  Type{' '}
                  <span style={{
                    color: '#DC2626', fontFamily: 'monospace',
                    background: '#FEF2F2', padding: '2px 6px', borderRadius: '4px'
                  }}>DELETE USER</span>
                  {' '}to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE USER"
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: `1.5px solid ${deleteConfirmText === 'DELETE USER' ? '#10B981' : '#E5E7EB'}`,
                    borderRadius: '6px', fontSize: '14px',
                    fontFamily: 'monospace', letterSpacing: '1px',
                    boxSizing: 'border-box',
                    background: deleteConfirmText === 'DELETE USER' ? '#F0FDF4' : 'white'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  fontSize: '13px', fontWeight: '600', color: '#374151',
                  display: 'block', marginBottom: '6px'
                }}>
                  Your Admin 2FA Code
                  <span style={{ color: '#9CA3AF', fontWeight: '400', marginLeft: '4px' }}>
                    (required if 2FA is enabled on your admin account)
                  </span>
                </label>
                <input
                  type="text"
                  value={adminTotpCode}
                  onChange={e => setAdminTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code from your authenticator"
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: `1.5px solid ${adminTotpCode.length === 6 ? '#10B981' : '#E5E7EB'}`,
                    borderRadius: '6px', fontSize: '16px',
                    fontFamily: 'monospace', letterSpacing: '4px', textAlign: 'center',
                    boxSizing: 'border-box',
                    background: adminTotpCode.length === 6 ? '#F0FDF4' : 'white'
                  }}
                  maxLength={6}
                />
                <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '4px 0 0' }}>
                  Leave blank if your admin account does not have 2FA enabled
                </p>
              </div>

              {error && (
                <div style={{
                  background: '#FEF2F2', border: '1px solid #FECACA',
                  borderRadius: '6px', padding: '10px 14px',
                  color: '#DC2626', fontSize: '13px', marginBottom: '16px'
                }}>⚠️ {error}</div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={closeDeleteModal}
                  disabled={deleteLoading}
                  style={{
                    flex: 1, padding: '12px', border: '1.5px solid #E5E7EB',
                    borderRadius: '6px', background: 'white', cursor: 'pointer',
                    fontSize: '14px', fontWeight: '600', color: '#374151'
                  }}
                >Cancel</button>
                <button
                  onClick={handleDeleteUser}
                  disabled={!canConfirmDelete}
                  style={{
                    flex: 1, padding: '12px', border: 'none', borderRadius: '6px',
                    cursor: canConfirmDelete ? 'pointer' : 'not-allowed',
                    fontSize: '14px', fontWeight: '700', color: 'white',
                    background: canConfirmDelete ? '#DC2626' : '#FCA5A5',
                    transition: 'background 0.2s'
                  }}
                >
                  {deleteLoading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <span style={{
                        width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.4)',
                        borderTopColor: 'white', borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite', display: 'inline-block'
                      }} />
                      Deleting...
                    </span>
                  ) : 'Delete Permanently'}
                </button>
              </div>
              <p style={{
                textAlign: 'center', fontSize: '11px', color: '#9CA3AF', margin: '12px 0 0'
              }}>
                This action is logged in the admin audit trail
              </p>
            </div>
          </div>
        )}

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      </div>
    </AdminLayout>
  );
};

export default UserManagement;
